/**
 * Supabase client with graceful fallback when env vars are not configured.
 * When not configured, all operations return mock-friendly results.
 *
 * Env var resolution priority:
 *   1) REACT_APP_*  — Create React App standard (Hostinger production build)
 *   2) SUPABASE_*   — Krisspy/Sandpack injection (development)
 *   3) VITE_*       — fallback for legacy .env files
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.SUPABASE_URL ||
  process.env.VITE_SUPABASE_URL ||
  '';

const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.SUPABASE_ANON_KEY ||
  process.env.VITE_SUPABASE_ANON_KEY ||
  '';

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

// Create a real client if configured, otherwise a stub that won't throw on import
let _supabase: SupabaseClient;

if (isSupabaseConfigured) {
  _supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
} else {
  // Minimal stub so imports don't crash; all calls return empty/error results
  console.warn(
    '[Supabase] No credentials found. App is running in MOCK mode. ' +
    'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable Supabase.'
  );
  _supabase = {
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      onAuthStateChange: (_event: any, _cb: any) => ({
        data: { subscription: { unsubscribe: () => {} } },
      }),
      signInWithPassword: async () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      signUp: async () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      signOut: async () => ({ error: null }),
      updateUser: async () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
    },
    from: (_table: string) => ({
      select: () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      insert: () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      update: () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      delete: () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      eq: () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
      order: () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
    }),
    functions: {
      invoke: async () => ({ data: null, error: { message: 'Supabase není nakonfigurován' } }),
    },
  } as unknown as SupabaseClient;
}

export const supabase = _supabase;
export default supabase;
